import classNames from 'classnames';
import React, { ReactNode } from 'react';
import Tooltip from 'ui/Tooltip';

export enum DescriptionListMode {
  Default = 'default', // default padding is used
  DefaultCompact = 'DefaultCompact', // compact padding is used
  Underneath = 'Underneath',
}

interface ActionItem {
  title?: string; // tooltip text for the action
  isVisible?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  element?: ReactNode; // rendered element with the onClick handler already attached
}
export interface DefinitionItem {
  term: string;
  definition: ReactNode;
  className?: string;
  classNameDT?: string;
  classNameDD?: string;
  mode?: DescriptionListMode; // the mode defines how the padding is applied
  isVisible?: boolean;
  actions?: ActionItem[];
}

/**
 * Render a single label and definition.
 */
function DescriptionListItem({
  definition,
  term,
  className,
  classNameDT,
  classNameDD,
  mode = DescriptionListMode.Default,
  isVisible = true,
  actions,
}: DefinitionItem): JSX.Element {
  if (!isVisible) {
    return <></>;
  }

  return (
    <div
      className={classNames('leading-6', className, {
        'py-4': mode === DescriptionListMode.Default,
        'py-2': mode === DescriptionListMode.Default,
        'sm:flex sm:gap-4 sm:px-0': [DescriptionListMode.Default, DescriptionListMode.DefaultCompact].includes(mode),
        'py-3': mode === DescriptionListMode.Underneath,
      })}
    >
      <dt
        className={classNames(classNameDT, 'font-medium text-gray-900 overflow-x-hidden', {
          'sm:w-1/3 shrink-0': [DescriptionListMode.Default, DescriptionListMode.DefaultCompact].includes(mode),
        })}
      >
        {term}
      </dt>

      <dd className={classNames(classNameDD, 'text-gray-700 sm:mt-0 relative w-full')}>
        {definition}
        {actions && actions.length > 0 && (
          <div className='absolute right-0 top-0 flex gap-x-1 items-center'>
            {actions
              .filter(action => action.isVisible !== false)
              .map((action, index) => (
                <>
                  {action.onClick && action.icon && (
                    <Tooltip content={action.title} key={index}>
                      <button type='button' onClick={action.onClick} title={action.title}>
                        {action.icon}
                      </button>
                    </Tooltip>
                  )}

                  {action.element && (
                    <Tooltip content={action.title} key={index}>
                      <div>{action.element}</div>
                    </Tooltip>
                  )}
                </>
              ))}
          </div>
        )}
      </dd>
    </div>
  );
}

export default DescriptionListItem;
